/**
 * @module lib/streams
 * @author Charles Blais <charles.blais@canada.ca>
 * 
 * Streams handler routines
 */

import moment from 'moment';

import { units } from '../constants';

import { WaveformTrace, WaveformStream } from '../typings/waveform-plot';

/**
 * From trace header information, we get the units for display
 * unknown units are return blank
 * @param {WaveformTrace} trace 
 * @returns {string}
 */
export const getTraceUnits = (
  trace: WaveformTrace
): string => {
  const inst_code = trace.header.channel.charAt(1);
  const orient_code = trace.header.channel.charAt(2);
  if( inst_code in units ){
    if( orient_code in units[inst_code] ){
      return units[inst_code][orient_code];
    }
  }
  return '';
}
 
 
/**
 * Search for all location codes that match the network, station and channel
 * @param {Array} streams 
 * @param {Object} header 
 */
export const getLocationCodes = (
  stream: WaveformStream,
  network: string,
  station: string,
  channel: string,
): string[] => {
  return stream.filter(t => t.header.network === network && 
      t.header.station === station && 
      t.header.channel === channel
    )
    .map(t => t.header.location)
    .sort();
};
 
 
/**
 * Get the minimum starttime of all traces
 */
export const getStreamsStarttime = (
  stream: WaveformStream,
): moment.Moment => {
  return stream.reduce((min, t) => t.header.starttime < min ? t.header.starttime : min, stream[0].header.starttime);
};
 
 
/**
 * Get the maximum endtime of all traces
 */
export const getStreamsEndtime = (
  stream: WaveformStream,
): moment.Moment => {
  return stream.reduce((max, t) => t.header.endtime > max ? t.header.endtime : max, stream[0].header.endtime);
};
 
 
/**
 * Set streams to equal start and endtime for equal plotting range.  Missing
 * data is flagged with null.
 * @param {WaveformTrace} streams 
 */
export const trimStreams = (
   streams: WaveformStream,
   starttime?: moment.Moment,
   endtime?: moment.Moment,
): WaveformStream => {
  const stime = (starttime) ? starttime : getStreamsStarttime(streams);
  const etime = (endtime) ? endtime : getStreamsEndtime(streams);
  return streams.map(trace => {
    const spad = Math.round(
      trace.header.starttime.diff(stime)/1000/trace.header.delta);
    const epad = Math.round(
      etime.diff(trace.header.endtime)/1000/trace.header.delta);
    trace.header.starttime = stime;
    trace.header.endtime = etime;
    // if the stream does not start with the same starttime, we need to pad it
    if(spad > 0) {
      trace.data = (new Array(spad).fill(null)).concat(trace.data);
    } else if(spad < 0) {
      // we have more data so we trunk the data by the pad
      trace.data = trace.data.slice(Math.abs(spad));
    }
    // trim the endtime
    if(epad > 0) {
      trace.data = trace.data.concat(new Array(epad).fill(null));
    } else if(epad < 0) {
      trace.data = trace.data.slice(0, epad);
    }
    return trace;
  });
};
 
 
/**
 * Generate an ID from the tract
 * @param {WaveformTrace} trace 
 * @param {bool} removeLocation - clear location code
 */
export const getWaveformID = (
  trace: WaveformTrace,
  removeLocation: Boolean = false,
): string => {
   const loc = removeLocation ? '' : trace.header.location;
   return `${trace.header.network}.${trace.header.station}.${loc}.${trace.header.channel}`;
}
